import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { Link } from "wouter";
import { ArrowLeft, Stethoscope, CheckCircle } from "lucide-react";

const PILLARS = [
  {
    title: "Expert Clinical Practice",
    desc: "Provides advanced clinical pharmacy practice at consultant level. Leads complex medicines management, operates as an expert prescriber in specialist area, and is the clinical authority for medicines within their specialty.",
    examples: [
      "Leading a rheumatology pharmacy clinic independently (DMARD monitoring, biologic initiation/review)",
      "Expert clinical advice to MDT, CCG/ICB, and formulary committees",
      "Management of complex, high-risk medicines (biologics, immunosuppressants, black triangle drugs)",
    ],
  },
  {
    title: "Leadership & Management",
    desc: "Leads the strategic development of pharmacy services, influences organisational policy, and manages teams and budgets at a senior level.",
    examples: [
      "Leading the rheumatology pharmacy service across a trust or network",
      "Board-level representation for medicines governance",
      "Budget management for high-cost medicines spend",
    ],
  },
  {
    title: "Education, Training & Development",
    desc: "Leads education and training for pharmacy staff and the wider healthcare team. Acts as a regional or national expert resource.",
    examples: [
      "Educational supervisor for Band 7 pharmacists and prescribing trainees",
      "Regional training lead for rheumatology pharmacy practice",
      "Teaching on postgraduate pharmacy programmes",
    ],
  },
  {
    title: "Research & Development",
    desc: "Generates and applies new evidence. Leads audit, QI, and research initiatives at organisational or national level.",
    examples: [
      "Principal investigator or co-investigator on clinical trials in rheumatology",
      "National audit lead for biosimilar switching outcomes",
      "Peer-reviewed publications on rheumatology pharmacy topics",
    ],
  },
];

export default function ConsultantPharmacist() {
  return (
    <PharmacistLayout title="Consultant Pharmacist Role">
      <div className="space-y-6 max-w-4xl">
        <Link href="/pharmacist/career">
          <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Career Pathways
          </button>
        </Link>

        <div className="bg-gradient-to-br from-rose-800 to-rose-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Stethoscope className="h-7 w-7 text-rose-300" />
            <div>
              <h2 className="text-xl font-bold">Consultant Pharmacist</h2>
              <p className="text-rose-300 text-sm">Band 8c/8d · Four Pillars of Practice</p>
            </div>
          </div>
          <p className="text-rose-100 text-sm leading-relaxed">
            The Consultant Pharmacist role is the most senior clinical pharmacy grade in the NHS. Consultants operate across four pillars of practice, with a significant proportion of their time in direct clinical practice. The RPS Consultant Pharmacist credential validates practice at this level.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-3">What Makes a Consultant Pharmacist?</h3>
          <div className="space-y-3 text-sm text-slate-600">
            <p>Consultant Pharmacists are experts in their specialty, working with the most complex patients and taking responsibility for service development and innovation. Key differentiators from Band 8a/8b roles:</p>
            <ul className="space-y-1.5">
              {[
                "Practice autonomously at advanced level, often leading specialist clinics without direct medical supervision",
                "Hold or actively pursuing the RPS Consultant Pharmacist credential",
                "Influence medicines policy at organisational, regional, or national level",
                "Significant contribution to research, publications, or national guidelines",
                "National/international reputation in their specialist field",
              ].map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PILLARS.map((pillar, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <h4 className="font-bold text-slate-900 mb-2">{pillar.title}</h4>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">{pillar.desc}</p>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Examples in Rheumatology:</p>
              <ul className="space-y-1">
                {pillar.examples.map((e, j) => (
                  <li key={j} className="flex items-start gap-2 text-xs text-slate-600">
                    <span className="text-rose-400 shrink-0 mt-0.5">•</span>
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-bold text-slate-900 mb-3">Getting There — Development Steps</h4>
          <ol className="space-y-2">
            {[
              "Achieve Band 8a/8b with a strong specialist portfolio and NMP qualification",
              "Publish service evaluations, audits, or case series — peer-reviewed if possible",
              "Engage with BSPAR, RPS Pharmacy Board, NHSE pharmacy networks — build your national presence",
              "Achieve RPS Advanced Credentialing as a stepping stone",
              "Identify a mentor who is an existing Consultant Pharmacist in your specialty",
              "Build your APPF portfolio with evidence across all four pillars",
              "Apply for RPS Consultant Pharmacist credential when evidence is strong",
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                <span className="shrink-0 h-5 w-5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          Consultant Pharmacist information is based on NHS England guidance and the RPS Consultant Pharmacist credentialing framework. Band 8c and 8d salaries are subject to AfC job evaluation and vary by organisation. For RPS credentialing details, see <a href="https://www.rpharms.com/development/rps-faculty" target="_blank" rel="noopener noreferrer" className="underline hover:text-violet-600">rpharms.com/development/rps-faculty</a>.
        </div>
      </div>
    </PharmacistLayout>
  );
}
